import React from 'react';
import type { EventStatus, DeliverStatus, PaymentStatus } from '../../api/types';
import { formatCurrency } from '../../utils/format';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'gold' | 'muted';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
}

const variantStyles: Record<BadgeVariant, React.CSSProperties> = {
  default:  { background: '#6B7280', color: '#FFFFFF' },
  primary:  { background: 'var(--color-primary)', color: '#FFFFFF' },
  success:  { background: 'var(--color-success)', color: '#FFFFFF' },
  warning:  { background: 'var(--color-warning)', color: '#111827' },
  error:    { background: 'var(--color-error)', color: '#FFFFFF' },
  gold:     { background: '#D97706', color: '#FFFFFF' },
  muted:    { background: '#6B7280', color: '#FFFFFF' },
};

export function Badge({ children, variant = 'default', size = 'sm', className = '' }: BadgeProps) {
  const padding = size === 'sm' ? '0.125rem 0.5rem' : '0.25rem 0.625rem';
  const fontSize = size === 'sm' ? 'var(--text-xs)' : 'var(--text-sm)';

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding,
        fontSize,
        fontWeight: 500,
        borderRadius: 'var(--radius-full)',
        whiteSpace: 'nowrap',
        letterSpacing: '0.01em',
        ...variantStyles[variant],
      }}
    >
      {children}
    </span>
  );
}

const STATUS_LABELS: Record<EventStatus, string> = {
  PREVENTIVO: 'Preventivo',
  CONTRATTO: 'Contratto',
  CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione',
  CONSEGNATO: 'Consegnato',
  ARCHIVIATO: 'Archiviato',
};

const STATUS_VARIANTS: Record<EventStatus, BadgeVariant> = {
  PREVENTIVO: 'muted',
  CONTRATTO: 'primary',
  CONFERMATO: 'success',
  IN_LAVORAZIONE: 'warning',
  CONSEGNATO: 'gold',
  ARCHIVIATO: 'default',
};

export function EventStatusBadge({ status, size }: { status: EventStatus; size?: BadgeSize }) {
  return <Badge variant={STATUS_VARIANTS[status]} size={size}>{STATUS_LABELS[status]}</Badge>;
}

const DELIVER_LABELS: Record<DeliverStatus, string> = {
  IN_LAVORAZIONE: 'In lavorazione',
  PRONTO: 'Pronto',
  CONSEGNATO: 'Consegnato',
};
const DELIVER_VARIANTS: Record<DeliverStatus, BadgeVariant> = {
  IN_LAVORAZIONE: 'warning',
  PRONTO: 'primary',
  CONSEGNATO: 'success',
};

export function DeliverStatusBadge({ status }: { status: DeliverStatus }) {
  return <Badge variant={DELIVER_VARIANTS[status]}>{DELIVER_LABELS[status]}</Badge>;
}

const PAY_LABELS: Record<PaymentStatus, string> = {
  DA_PAGARE: 'Da pagare',
  PAGATO: 'Pagato',
  PARZIALE: 'Parziale',
};
const PAY_VARIANTS: Record<PaymentStatus, BadgeVariant> = {
  DA_PAGARE: 'warning',
  PAGATO: 'success',
  PARZIALE: 'gold',
};

export function PaymentStatusBadge({ status, partialPayments, totalFee }: { status: PaymentStatus; partialPayments?: Array<{ amount: number; paidAt: string }> | null; totalFee?: number | null }) {
  const label = PAY_LABELS[status];
  if (status === 'PARZIALE' && partialPayments?.length) {
    const total = partialPayments.reduce((s, p) => s + p.amount, 0);
    const parts = [label, formatCurrency(total)];
    if (totalFee != null && totalFee > 0 && totalFee !== total) {
      parts.push(`/ ${formatCurrency(totalFee)}`);
    }
    return <Badge variant={PAY_VARIANTS[status]}>{parts.join(' ')}</Badge>;
  }
  return <Badge variant={PAY_VARIANTS[status]}>{label}</Badge>;
}
